// QuickActionsToolbar.jsx - VERSIÓN CLEAN SOLO BOTONES
import React, {
  useRef,
  useCallback,
  useMemo,
  useDeferredValue,
} from "react";
import { motion } from "framer-motion";
import Icon from "../../../components/appIcon";

// 🎨 DESIGN SYSTEM
const createAtomicDesignSystem = () => ({
  colors: {
    primary: {
      bg: "bg-gradient-to-r from-slate-900/95 via-gray-900/95 to-slate-900/95",
      border: "border border-gray-700/50 backdrop-blur-2xl",
    },
  },
  animations: {
    entrance: {
      initial: { opacity: 0, y: -30, scale: 0.95 },
      animate: { opacity: 1, y: 0, scale: 1 },
      exit: { opacity: 0, y: -20, scale: 0.95 },
      transition: { type: "spring", damping: 25, stiffness: 400 },
    },
    micro: {
      hover: { scale: 1.02, y: -2 },
      tap: { scale: 0.98 },
      transition: { type: "spring", damping: 15, stiffness: 400 },
    },
  },
});

// 🔁 OPTIMIZACIÓN SUAVE PARA EL CONTADOR SELECCIONADO
const usePerformanceOptimizer = (selectedCount) => {
  const deferredSelectedCount = useDeferredValue(selectedCount);

  return useMemo(
    () => ({
      selectedCount: deferredSelectedCount,
      isStale: deferredSelectedCount !== selectedCount,
    }),
    [deferredSelectedCount, selectedCount]
  );
};

// 🧩 COMPONENTE BOTÓN
const QuantumButton = React.memo(
  ({
    children,
    onClick,
    variant = "ghost",
    icon,
    badge,
    loading = false,
    disabled = false,
    ...props
  }) => {
    const designSystem = createAtomicDesignSystem();

    const variants = {
      // Exportar - Verde
      export:
        "bg-gradient-to-r from-emerald-600 to-green-600 shadow-lg shadow-emerald-500/30 border-emerald-400/30 hover:from-emerald-500 hover:to-green-500",
      // Importar - Amarillo
      import:
        "bg-gradient-to-r from-amber-500 to-yellow-600 shadow-lg shadow-amber-500/30 border-amber-400/30 hover:from-amber-400 hover:to-yellow-500",
      // Eliminar - Rojo
      delete:
        "bg-gradient-to-r from-red-600 to-rose-600 shadow-lg shadow-red-500/30 border-red-400/30 hover:from-red-500 hover:to-rose-500",
      // Agregar - Azul
      add: "bg-gradient-to-r from-blue-600 to-cyan-600 shadow-lg shadow-blue-500/30 border-blue-400/30 hover:from-blue-500 hover:to-cyan-500",
      // Filtrar - Morado
      filter:
        "bg-gradient-to-r from-purple-600 to-violet-600 shadow-lg shadow-purple-500/30 border-purple-400/30 hover:from-purple-500 hover:to-violet-500",
      // Vender - Verde brillante
      sale: "bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg shadow-green-500/30 border-green-400/30 hover:from-green-400 hover:to-emerald-500",
      // Refresh - Azul
      refresh:
        "bg-gradient-to-r from-blue-600 to-cyan-600 shadow-lg shadow-blue-500/30 border-blue-400/30 hover:from-blue-500 hover:to-cyan-500",
      // Ghost
      ghost:
        "bg-gray-800/40 border-gray-600/30 backdrop-blur-sm hover:bg-gray-700/50",
    };

    return (
      <motion.button
        {...designSystem.animations.micro}
        onClick={onClick}
        disabled={loading || disabled}
        className={`
          relative flex items-center justify-center space-x-2 px-4 py-3 
          rounded-2xl border text-white font-semibold
          transition-all duration-300 overflow-hidden group
          ${variants[variant]}
          ${loading || disabled
            ? "opacity-50 cursor-not-allowed"
            : "hover:shadow-xl"
          }
        `}
        {...props}
      >
        {/* Efecto de brillo dinámico */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 translate-x-[-150%] group-hover:translate-x-[150%] transition-transform duration-1000" />

        {/* Badge (para selección / cantidad) */}
        {badge != null && badge > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold border-2 border-gray-900"
          >
            {badge}
          </motion.span>
        )}

        {/* Icono con estado de carga */}
        {icon && (
          <motion.div
            animate={loading ? { rotate: 360 } : {}}
            transition={
              loading
                ? { duration: 1, repeat: Infinity, ease: "linear" }
                : {}
            }
          >
            <Icon
              name={loading ? "Loader" : icon}
              size={18}
              className="relative z-10 group-hover:scale-110 transition-transform duration-300"
            />
          </motion.div>
        )}

        {/* Contenido */}
        <span className="relative z-10 whitespace-nowrap">{children}</span>
      </motion.button>
    );
  }
);

// 🎯 TOOLBAR PRINCIPAL - SOLO BOTONES CENTRADOS
const QuickActionsToolbar = React.memo(
  ({
    onAction,
    selectedCount = 0,
    totalItems = 0, // sigue en la firma por compatibilidad, aunque no se use
    userRole = "admin",
    viewMode = "grid", // ignorado, dejamos compatibilidad con el padre
    onViewModeChange, // ignorado
    isLoading = false,
    inventoryData = [], // ignorado
    selectedItems = [],
    className = "",
  }) => {
    const toolbarRef = useRef(null);
    const designSystem = useMemo(() => createAtomicDesignSystem(), []);
    const { selectedCount: deferredSelectedCount } =
      usePerformanceOptimizer(selectedCount);

    // 🚀 MANEJADORES DE ACCIONES
    const handleExport = useCallback(() => {
      onAction("export", {
        selectedItems: selectedCount > 0 ? selectedItems : null,
        timestamp: Date.now(),
      });
    }, [onAction, selectedCount, selectedItems]);

    const handleImport = useCallback(() => {
      onAction("import", {
        type: "inventory",
        userRole,
      });
    }, [onAction, userRole]);

    const handleDelete = useCallback(() => {
      if (selectedCount > 0) {
        onAction("delete_selected", {
          selectedItems,
          confirmation: true,
        });
      }
    }, [onAction, selectedCount, selectedItems]);

    const handleAdd = useCallback(() => {
      onAction("add_item", {
        userRole,
        context: "inventory",
      });
    }, [onAction, userRole]);

    const handleFilter = useCallback(() => {
      onAction("filter", {
        type: "open_modal",
        currentFilters: {},
      });
    }, [onAction]);

    const handleSale = useCallback(() => {
      onAction("sale", {
        userRole,
        context: "inventory",
        hasSelection: selectedCount > 0,
        selectedItems: selectedCount > 0 ? selectedItems : null,
      });
    }, [onAction, userRole, selectedCount, selectedItems]);

    const handleRefresh = useCallback(() => {
      onAction("refresh", {
        timestamp: Date.now(),
        userRole,
      });
    }, [onAction, userRole]);

    const handleBulkUpdate = useCallback(() => {
      if (selectedCount > 0) {
        onAction("bulk_update", {
          selectedItems,
          userRole,
        });
      }
    }, [onAction, selectedCount, selectedItems, userRole]);

    return (
      <>
        {/* TOOLBAR SOLO BOTONES CENTRADOS */}
        <motion.div
          ref={toolbarRef}
          {...designSystem.animations.entrance}
          className={`
            ${designSystem.colors.primary.bg}
            ${designSystem.colors.primary.border}
            rounded-3xl shadow-2xl shadow-blue-500/10
            px-6 py-4 backdrop-blur-2xl
            hover:shadow-blue-500/20 transition-shadow duration-500
            ${className}
          `}
        >
          <div className="flex items-center justify-center">
            <div className="flex flex-wrap items-center justify-center gap-3">

              {/* Exportar */}
              <QuantumButton
                onClick={handleExport}
                variant="export"
                icon="Download"
                loading={isLoading}
              >
                <span className="hidden sm:inline">Exportar</span>
                <span className="sm:hidden">Export</span>
              </QuantumButton>

              {/* Importar */}
              <QuantumButton
                onClick={handleImport}
                variant="import"
                icon="Upload"
                loading={isLoading}
              >
                <span className="hidden sm:inline">Importar</span>
                <span className="sm:hidden">Import</span>
              </QuantumButton>

              {/* Eliminar (solo con selección) */}
              {deferredSelectedCount > 0 && (
                <QuantumButton
                  onClick={handleDelete}
                  variant="delete"
                  icon="Trash2"
                  badge={deferredSelectedCount}
                >
                  <span className="hidden sm:inline">Eliminar</span>
                  <span className="sm:hidden">Del</span>
                </QuantumButton>
              )}

              {/* Agregar */}
              <QuantumButton
                onClick={handleAdd}
                variant="add"
                icon="Plus"
              >
                <span className="hidden sm:inline">Agregar</span>
                <span className="sm:hidden">Add</span>
              </QuantumButton>

              {/* Filtrar */}
              <QuantumButton
                onClick={handleFilter}
                variant="filter"
                icon="Filter"
              >
                <span className="hidden sm:inline">Filtrar</span>
                <span className="sm:hidden">Filter</span>
              </QuantumButton>

              {/* Vender */}
              <QuantumButton
                onClick={handleSale}
                variant="sale"
                icon="ShoppingCart"
                disabled={isLoading}
              >
                <span className="hidden sm:inline">Vender</span>
                <span className="sm:hidden">Sale</span>
              </QuantumButton>

              {/* Actualizar */}
              <QuantumButton
                onClick={handleRefresh}
                variant="refresh"
                icon="RefreshCw"
                loading={isLoading}
              >
                <span className="hidden sm:inline">Actualizar</span>
                <span className="sm:hidden">Refresh</span>
              </QuantumButton>

              {/* Edición masiva (solo con selección) */}
              {deferredSelectedCount > 0 && (
                <QuantumButton
                  onClick={handleBulkUpdate}
                  variant="import" // reuso estilo amarillo
                  icon="Edit3"
                  badge={deferredSelectedCount}
                >
                  <span className="hidden sm:inline">Editar</span>
                  <span className="sm:hidden">Edit</span>
                </QuantumButton>
              )}

            </div>
          </div>
        </motion.div>

        {/* Estilos globales para animaciones (por si en el futuro reutilizas) */}
        <style>
          {`
  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
    50% { box-shadow: 0 0 30px rgba(59, 130, 246, 0.6); }
  }
  @keyframes success-glow {
    0%, 100% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.3); }
    50% { box-shadow: 0 0 30px rgba(16, 185, 129, 0.6); }
  }
  .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
  .animate-success-glow { animation: success-glow 2s ease-in-out infinite; }
`}
        </style>
      </>
    );
  }
);

QuickActionsToolbar.displayName = "QuickActionsToolbar";

export default QuickActionsToolbar;
